import React from "react";
import { Stack } from "@chakra-ui/react";
import ResponsiveFormStack from "../common/ResponsiveFormStack";
import {
  InputFormControl,
  PreviewDropdownFormControl,
} from "../common/FormControlComponents";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { AddressType } from "../../types/address.types";
import {
  ModuleChangeStatus,
  ModuleSectionHeader,
} from "../common/ModuleToggleIndicator";

interface MailingAddressProps {
  addressType: AddressType;
  city: string;
  street: string;
  postalCode: string;
  country: string;
  phone: string;
  handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement>;
  setFieldValue: Function;
  moduleChange?: ModuleChangeStatus;
  isEditMode?: boolean;
}

const MailingAddress: React.FC<MailingAddressProps> = (props) => {
  const { i18n } = useLingui();

  // Determine if this is a soft required field (module was enabled after registration)
  const isSoftRequired = React.useMemo(() => {
    return props.isEditMode && props.moduleChange === "enabled";
  }, [props.isEditMode, props.moduleChange]);

  // Check if any field in the mailing address module has been filled
  // If soft required, only require if user starts filling any field
  // Note: country might have default value, so only count it if addressType is also selected
  const hasAnyFieldFilled = React.useMemo(() => {
    // If addressType is selected, then user has started filling
    if (props.addressType && props.addressType !== AddressType.NONE) {
      return true;
    }
    // Otherwise, check other fields (but not country, as it might have default value)
    return !!(
      props.country?.trim() ||
      props.city?.trim() ||
      props.street?.trim() ||
      props.postalCode?.trim() ||
      props.phone?.trim()
    );
  }, [
    props.country,
    props.addressType,
    props.city,
    props.street,
    props.postalCode,
    props.phone,
  ]);

  // Soft required: only required if user has started filling the module
  // If soft required and user starts filling, it becomes hard required
  const isAddressTypeSoftRequired = isSoftRequired && !hasAnyFieldFilled;
  const isAddressTypeHardRequired =
    !isSoftRequired || (isSoftRequired && hasAnyFieldFilled);

  // Determine default preview text - show "Originally not selected" if soft required and no value selected
  const defaultPreviewText = React.useMemo(() => {
    const isNotSelected =
      !props.addressType ||
      props.addressType === AddressType.NONE ||
      props.addressType === undefined;

    if (isSoftRequired && isNotSelected) {
      return i18n._("Originally not selected");
    }
    return i18n._("Please select!");
  }, [isSoftRequired, props.addressType, i18n]);

  return (
    <Stack>
      <ModuleSectionHeader
        title={<Trans>Mailing address</Trans>}
        change={props.moduleChange}
      />
      <ResponsiveFormStack
        firstElement={
          <PreviewDropdownFormControl
            id={"addressType"}
            label={i18n._("Type of address")}
            values={[
              { id: AddressType.WORK, leftText: i18n._("Workplace address") },
              { id: AddressType.HOME, leftText: i18n._("Home") },
            ]}
            fieldName="address.addressType"
            setFieldValue={props.setFieldValue}
            selectedId={
              props.addressType && props.addressType !== AddressType.NONE
                ? props.addressType
                : undefined
            }
            isRequired={isAddressTypeHardRequired}
            isSoftRequired={isAddressTypeSoftRequired}
            defaultPreviewText={defaultPreviewText}
            testId="mailing-address-type-dropdown"
          />
        }
      />
      <ResponsiveFormStack
        firstElement={
          <InputFormControl
            id="mailingCountry"
            label={i18n._("Country")}
            inputType="text"
            name="address.country"
            value={props.country}
            onChange={props.handleChange}
            testId="mailing-country-input"
          />
        }
        secondElement={
          <InputFormControl
            id="mailingPostalCode"
            label={i18n._("Postal code")}
            inputType="text"
            name="address.postalCode"
            value={props.postalCode}
            onChange={props.handleChange}
            testId="mailing-postal-code-input"
          />
        }
      />
      <ResponsiveFormStack
        firstElement={
          <InputFormControl
            id="mailingCity"
            label={i18n._("City")}
            inputType="text"
            name="address.city"
            value={props.city}
            onChange={props.handleChange}
            testId="mailing-city-input"
          />
        }
        secondElement={
          <InputFormControl
            id="mailingAddress"
            label={i18n._("Address")}
            inputType="text"
            name="address.street"
            value={props.street}
            onChange={props.handleChange}
            testId="mailing-address-input"
          />
        }
      />
      <ResponsiveFormStack
        firstElement={
          <InputFormControl
            id="mailingMobile"
            label={i18n._("Mobile")}
            inputType="tel"
            name="address.phone"
            value={props.phone}
            onChange={props.handleChange}
            testId="mailing-mobile-input"
          />
        }
      />
    </Stack>
  );
};

export default MailingAddress;
